import { Logger, LogLevel } from '@web-publish-id/logger'
import { GameSDKAuthToken, GameSDKInitOptions, GameSDKLoginOptions } from './types'
import sharedConfig, { injectClientId, injectSecretConfig } from '@web-publish-id/shared/config'
import { getGameTokenInfo, removeGameTokenInfo, setGameTokenInfo } from '@web-publish-id/shared/caches'
import { publishAccountAuthClear, publishAccountRefreshToken } from '@web-publish-id/shared/apis'
import { getPageLangCode } from '@web-publish-id/shared'
import { HttpServerError } from '@web-publish-id/shared/http'
import { ClientAuthSearchParams } from 'packages/shared/src/types'
import { GameTracker } from './track'
import { SDK_VERSION } from './config'
import { createSessionId } from '@web-publish-id/shared/helper'

// 认证地址
const authUrl = sharedConfig.origin + '/web/auth'

export class GameCore {
  protected logger!: Logger
  protected options!: GameSDKInitOptions
  protected tracker: GameTracker

  // 记录授权时间戳
  protected authStamp = 0

  // 授权结果
  protected authResult: GameSDKAuthToken

  constructor() {
    this.logger = new Logger({
      prefix: 'GameSDK',
      level: import.meta.env.PROD ? LogLevel.INFO : LogLevel.DEBUG,
    })
    this.tracker = new GameTracker()
  }

  /**
   * 初始化
   * @param options
   */
  public async init(options: GameSDKInitOptions) {
    const { secret, client_id, ...otherOptions } = options
    this.logger.info('init', { client_id, ...otherOptions })

    this.validateOptions(options)

    injectSecretConfig(secret)
    injectClientId(client_id)

    this.options = options

    this.tracker.setup()
    this.tracker.setCommonProperties({
      identify_id: '',
      client_id: client_id,
      sdk_version: SDK_VERSION,
    })

    this.logger.info('initialized')
  }

  /**
   * 登录
   */
  public login(options: GameSDKLoginOptions = {}) {
    this.logger.info('login')

    const url = this.getLoginUrl(options)
    window.location.href = url
  }

  /**
   * 获取游戏token
   */
  public async getToken(): Promise<GameSDKAuthToken> {
    this.logger.debug('getToken')

    // 如果有授权回调，且授权回调时间戳小于2分钟，则直接返回token
    if (this.authResult && this.authStamp && Date.now() - this.authStamp < 1000 * 60 * 2) {
      return this.authResult
    }

    // 获取缓存
    const info = getGameTokenInfo()
    if (!info) return null

    // 刷新token
    return await this.refreshToken(info.access_token).catch((e) => {
      if (e instanceof HttpServerError) {
        return e.toJson()
      }

      return { err: true, message: e.message }
    })
  }

  /**
   * 登出
   */
  public logout() {
    this.logger.info('logout')

    // 清除授权时间戳
    this.authStamp = 0
    this.authResult = null

    const token = getGameTokenInfo()
    if (token) {
      this.tracker.trackPublishAccountLogout(token.access_token)

      removeGameTokenInfo()
      return publishAccountAuthClear(token.access_token)
    }
  }

  /**
   * 获取登录url
   * @param params
   * @returns
   */
  protected getLoginUrl(params?: Partial<ClientAuthSearchParams>) {
    const redirectUri = params?.redirect_uri || window.location.origin + window.location.pathname

    const paramsObj = Object.entries(params || {}).reduce(
      (acc, [key, value]) => {
        if (value) acc[key] = String(value)
        return acc as Record<string, string>
      },
      {} as Record<string, string>,
    )

    const sessionId = createSessionId()

    const urlParams = new URLSearchParams({
      client_id: this.options.client_id,
      redirect_uri: redirectUri,
      language: this.options.language || getPageLangCode(),
      scopes: this.options.scopes,
      session_id: sessionId,
      ...paramsObj,
    })

    return authUrl + '?' + urlParams.toString()
  }

  /**
   * 验证初始化参数
   * @param options
   */
  protected validateOptions(options: GameSDKInitOptions) {
    const { secret, client_id } = options
    if (!client_id) {
      throw new Error('client_id is required')
    }
    if (!secret) {
      throw new Error('secret is required')
    }
  }

  /**
   * 刷新token
   * @param oldToken 旧token
   */
  protected async refreshToken(oldToken: string): Promise<GameSDKAuthToken> {
    this.tracker.trackPublishTokenRefresh(oldToken)

    const data = await publishAccountRefreshToken({ access_token: oldToken }).catch((e) => {
      const errorMsg = e instanceof Error ? e.message : String(e)
      this.tracker.trackPublishTokenRefreshFailed(oldToken, errorMsg)
      throw e
    })

    setGameTokenInfo(data)

    this.authStamp = Date.now()

    this.logger.debug('refreshToken success', data)

    return data
  }

  /**
   * 处理授权回调 在客户端中处理
   */
  public handleAuthCallback() {
    const urlParams = new URLSearchParams(window.location.search)
    const code = urlParams.get('code')
    const access_info = urlParams.get('access_info')
    if (!code && !access_info) return

    const state = urlParams.get('state')

    this.handleAuthResult({ code, access_info, state })

    // 清除url中的access_token参数
    urlParams.delete('access_info')
    urlParams.delete('code')
    urlParams.delete('state')
    window.history.replaceState({}, null, '?' + urlParams.toString())

    this.logger.info('got access token.')
  }

  /**
   * 处理授权结果
   * @param authResult
   */
  protected handleAuthResult(data: { code: string | number; access_info: string; state: string }) {
    const { code, access_info, state } = data

    const authResult: GameSDKAuthToken = {}

    // 处理code
    if (code) {
      authResult.code = Number(code)
      if (authResult.code !== 20000) authResult.err = true
    }

    // 处理access_info
    if (access_info) {
      try {
        const tokenInfo = JSON.parse(access_info)
        // 设置游戏token
        setGameTokenInfo(tokenInfo)

        Object.assign(authResult, tokenInfo)
      } catch (error) {
        this.logger.error('parse access_info error', access_info)
      }
    }

    this.logger.debug('authResult', authResult)

    this.authStamp = Date.now()
    this.authResult = authResult

    return authResult
  }
}

export const gameSdk = new GameCore()

export default gameSdk
